import { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import {
  IconAlertTriangleFilled,
  IconCircleCheckFilled,
  IconX,
} from '@tabler/icons-react';

const FONT = '"Public Sans", sans-serif';

const ALERT_TYPES = {
  success: {
    color: '#30d158',
    tint: 'rgba(48, 209, 88, 0.14)',
    icon: IconCircleCheckFilled,
    autoClose: true,
  },
  error: {
    color: '#ff453a',
    tint: 'rgba(255, 69, 58, 0.14)',
    icon: IconAlertTriangleFilled,
    autoClose: false,
  },
  loading: {
    color: '#0a84ff',
    tint: 'rgba(10, 132, 255, 0.14)',
    icon: null,
    autoClose: false,
  },
};

const DynamicIsland = forwardRef(function DynamicIsland(_, ref) {
  const [alerts, setAlerts] = useState([]);
  const [expanded, setExpanded] = useState(false);

  const hasAlerts = alerts.length > 0;
  const currentAlert = alerts[alerts.length - 1];
  const alertType = currentAlert ? ALERT_TYPES[currentAlert.type] : null;
  const AlertIcon = alertType?.icon;
  const message = currentAlert?.message?.trim();
  const canDismiss = hasAlerts && currentAlert.type !== 'loading';

  useImperativeHandle(ref, () => ({
    addAlert: (type, message) => {
      const newAlert = { id: Date.now(), type, message };
      setAlerts((prev) => [...prev, newAlert]);
      setExpanded(true);
    },
    clearAlerts: () => {
      setAlerts([]);
      setExpanded(false);
    },
  }));

  useEffect(() => {
    if (!hasAlerts || !alertType || !alertType.autoClose) return;
    const timer = setTimeout(() => {
      setAlerts((prev) => prev.filter((a) => a.id !== currentAlert.id));
      if (alerts.length <= 1) setExpanded(false);
    }, 3000);
    return () => clearTimeout(timer);
  }, [hasAlerts, alertType, currentAlert, alerts.length]);

  const handleClick = () => {
    if (!canDismiss) return;
    setAlerts((prev) => prev.filter((a) => a.id !== currentAlert.id));
    if (alerts.length <= 1) setExpanded(false);
  };

  return (
    <div
      role={hasAlerts ? (currentAlert.type === 'error' ? 'alert' : 'status') : undefined}
      aria-live={hasAlerts ? (currentAlert.type === 'error' ? 'assertive' : 'polite') : undefined}
      aria-atomic="true"
      title={hasAlerts ? message : undefined}
      tabIndex={canDismiss ? 0 : undefined}
      onClick={handleClick}
      onKeyDown={
        canDismiss
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleClick();
              }
            }
          : undefined
      }
      className={`relative flex items-center overflow-hidden select-none transition-all duration-300 ease-out ${
        hasAlerts ? 'gap-2.5 h-12 pl-2 pr-4' : 'h-2 px-0'
      } ${canDismiss ? 'cursor-pointer active:scale-[0.99]' : 'cursor-default'}`}
      style={{
        backgroundColor: hasAlerts ? 'rgba(18, 18, 18, 0.94)' : 'rgba(255, 255, 255, 0.06)',
        border: hasAlerts
          ? '1px solid rgba(255, 255, 255, 0.08)'
          : '1px solid rgba(255, 255, 255, 0.04)',
        borderRadius: '9999px',
        boxShadow: hasAlerts
          ? '0 12px 32px rgba(0, 0, 0, 0.5), inset 0 1px 0 rgba(255, 255, 255, 0.06)'
          : '0 0 12px rgba(255, 255, 255, 0.08)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        fontFamily: FONT,
        maxWidth: hasAlerts ? 'min(92vw, 560px)' : '160px',
        width: hasAlerts ? undefined : '160px',
        animation: hasAlerts ? `waterDrop 420ms cubic-bezier(0.34, 1.56, 0.64, 1)` : undefined,
        transformOrigin: 'top center',
      }}
    >
      {hasAlerts ? (
        <>
          <span
            className="flex items-center justify-center shrink-0 w-8 h-8 rounded-full"
            style={{ backgroundColor: alertType.tint }}
          >
            {currentAlert.type === 'loading' ? (
              <span
                aria-hidden="true"
                style={{
                  width: '15px',
                  height: '15px',
                  border: '2px solid rgba(255, 255, 255, 0.22)',
                  borderTopColor: '#fff',
                  borderRadius: '50%',
                  animation: 'islandSpin 0.8s linear infinite',
                }}
              />
            ) : (
              AlertIcon && <AlertIcon size={17} color={alertType.color} />
            )}
          </span>

          {expanded && (
            <span
              className="min-w-0 truncate font-semibold"
              style={{
                fontSize: '14px',
                lineHeight: '18px',
                letterSpacing: '-0.01em',
                color: 'rgba(255, 255, 255, 0.95)',
                animation: 'islandFadeIn 240ms ease-out',
              }}
            >
              {currentAlert.type === 'loading' ? message || 'Procesando…' : message}
            </span>
          )}

          {canDismiss && expanded && (
            <IconX size={16} color="rgba(255, 255, 255, 0.4)" className="shrink-0" />
          )}
        </>
      ) : (
        <span
          aria-hidden="true"
          className="w-full h-full rounded-full"
          style={{ backgroundColor: 'rgba(255, 255, 255, 0.18)' }}
        />
      )}

      <style>{`
        @keyframes islandSpin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes islandFadeIn {
          from { opacity: 0; transform: translateX(-4px); }
          to { opacity: 1; transform: translateX(0); }
        }
        @keyframes waterDrop {
          0% {
            opacity: 0;
            transform: scale(0.25, 0.25);
            filter: blur(6px);
          }
          55% {
            opacity: 1;
            transform: scale(1.06, 1.06);
            filter: blur(0);
          }
          100% {
            opacity: 1;
            transform: scale(1, 1);
            filter: blur(0);
          }
        }
      `}</style>
    </div>
  );
});

export default DynamicIsland;
